import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Truck, Sparkles, Layers, Snowflake, ArrowRight, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";

const WHATSAPP_URL =
  "https://api.whatsapp.com/send?phone=15874073024&text=Hi%20Garage%20Kings!%20%F0%9F%91%91%20I%27d%20like%20a%20quote.";

type Service = {
  icon: typeof Truck;
  title: string;
  blurb: string;
  cta: { label: string; href?: string; to?: string; external?: boolean };
  featured?: boolean;
};

const services: Service[] = [
  {
    icon: Truck,
    title: "Junk Removal & Hauling",
    blurb: "Single-item pickups to full cleanouts — sorted, donated, and recycled responsibly.",
    cta: { label: "Book Now", href: WHATSAPP_URL, external: true },
  },
  {
    icon: Sparkles,
    title: "Property Resets",
    blurb: "Deep clear, scrub, and organize. We pay cash on the spot for tools, bikes, and gear.",
    cta: { label: "Book Now", href: WHATSAPP_URL, external: true },
  },
  {
    icon: Layers,
    title: "Premium Epoxy & Polyaspartic Flooring",
    blurb: "Industrial-grade showroom floors installed in 1 day. Lifetime-tough against salt and hot tires.",
    cta: { label: "Learn More", to: "/epoxy-flooring" },
    featured: true,
  },
  {
    icon: Snowflake,
    title: "Seasonal Swaps & Detail Add-ons",
    blurb: "Tire storage rotation, wall wiping, pressure washing — keep the reset looking new.",
    cta: { label: "Book Now", href: WHATSAPP_URL, external: true },
  },
];

export const Services = () => (
  <section id="services" className="bg-background py-12 md:py-16">
    <div className="container">
      <div className="mb-10 text-center">
        <p className="mb-3 text-sm font-semibold uppercase tracking-[0.3em] text-gold-deep">What We Do</p>
        <h2 className="font-display text-4xl tracking-wide text-ink md:text-5xl">
          Four Services. One Crew. Zero Hassle.
        </h2>
        <p className="mx-auto mt-3 max-w-2xl text-muted-foreground">
          Edmonton's only true garage specialist — from haul-away to high-gloss floor.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {services.map((s, i) => (
          <motion.div
            key={s.title}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.4, delay: i * 0.06 }}
            className={`group flex h-full flex-col rounded-xl border-2 bg-card p-6 transition-all hover:-translate-y-1 hover:shadow-gold ${
              s.featured ? "border-gold shadow-gold" : "border-border hover:border-gold"
            }`}
          >
            <div
              className={`mb-4 inline-flex h-12 w-12 items-center justify-center rounded-full ${
                s.featured ? "bg-gold text-ink" : "bg-ink text-gold group-hover:bg-gold group-hover:text-ink"
              } transition-colors`}
            >
              <s.icon className="h-6 w-6" />
            </div>
            {s.featured && (
              <span className="mb-2 inline-block w-fit rounded-full bg-gold/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-gold-deep">
                Flagship
              </span>
            )}
            <h3 className="font-display text-xl tracking-wide text-ink">{s.title}</h3>
            <p className="mt-2 flex-1 text-sm text-muted-foreground">{s.blurb}</p>
            <div className="mt-5">
              {s.cta.to ? (
                <Link
                  to={s.cta.to}
                  className="inline-flex items-center gap-1 text-sm font-bold uppercase tracking-wider text-ink hover:text-gold-deep"
                >
                  {s.cta.label} <ArrowRight className="h-4 w-4" />
                </Link>
              ) : (
                <a
                  href={s.cta.href}
                  target={s.cta.external ? "_blank" : undefined}
                  rel={s.cta.external ? "noopener noreferrer" : undefined}
                  className="inline-flex items-center gap-1 text-sm font-bold uppercase tracking-wider text-ink hover:text-gold-deep"
                >
                  {s.cta.label} <ArrowRight className="h-4 w-4" />
                </a>
              )}
            </div>
          </motion.div>
        ))}
      </div>

      <div className="mt-10 text-center">
        <p className="mb-4 text-sm text-muted-foreground">
          Not sure what you need? Send a photo — we'll quote within the hour.
        </p>
        <Button asChild size="lg" className="h-12 bg-[#25D366] px-6 font-bold text-white hover:bg-[#1eb858]">
          <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
            <MessageCircle className="mr-2 h-5 w-5" /> Send Photo on WhatsApp
          </a>
        </Button>
      </div>
    </div>
  </section>
);